import { useEffect, useState } from "react";

interface AnimatedCheckmarkProps {
  /** Size in pixels (renders as a square). */
  size?: number;
  /** Color tokens: "oxy" (success — default), "jove", or a literal color value. */
  tone?: "oxy" | "jove" | "hydra";
  /** Delay in ms before the stroke starts drawing (useful for staggered lists). */
  delay?: number;
  /** Draw a ring around the check. Subtle depth on hero moments. */
  ringed?: boolean;
}

/**
 * SVG checkmark that draws itself on mount.
 *
 * The draw uses CSS `stroke-dasharray` + `stroke-dashoffset` transitioning
 * from the full length to 0 — the canonical technique for SVG line drawing
 * without any animation library.
 */
export function AnimatedCheckmark({
  size = 28,
  tone = "oxy",
  delay = 0,
  ringed = false,
}: AnimatedCheckmarkProps) {
  const [drawn, setDrawn] = useState(false);

  useEffect(() => {
    const id = window.setTimeout(() => setDrawn(true), delay);
    return () => window.clearTimeout(id);
  }, [delay]);

  const colorClass =
    tone === "oxy"
      ? "text-oxy"
      : tone === "jove"
        ? "text-jove"
        : "text-hydra";

  // Total stroke length of the check path, measured once — any longer
  // than the actual path is fine because `stroke-dashoffset` just shifts
  // the already-hidden tail off the end.
  const STROKE_LEN = 26;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${colorClass}`}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        className="absolute inset-0 h-full w-full"
      >
        {ringed && (
          <circle
            cx="16"
            cy="16"
            r="15"
            stroke="currentColor"
            strokeOpacity="0.2"
            strokeWidth="1.25"
            className="transition-opacity duration-300"
            style={{
              opacity: drawn ? 1 : 0,
              transitionDelay: `${Math.max(0, delay - 120)}ms`,
            }}
          />
        )}
        <path
          d="M9 16.5 L14 21.5 L23 11.5"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: STROKE_LEN,
            strokeDashoffset: drawn ? 0 : STROKE_LEN,
            transition: "stroke-dashoffset 420ms cubic-bezier(0.2, 0.8, 0.2, 1)",
          }}
        />
      </svg>
    </div>
  );
}
